import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

// Real project images - verified mapping
const images = {
  blueTileBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/fpgzjpp3_Before%202.jpeg",
  blueTileAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/iqdzyqzo_After%202.jpeg",
  // Stone/masonry pair - SWAPPED based on visual inspection
  stoneBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/926zc2jx_After%201.jpeg",
  stoneAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/v9fe3x5w_Before%201.jpeg",
  poolPrepBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/uz4ov2h2_Before%203.jpeg",
  poolPrepAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/lncc0qq7_After%203.jpeg",
};

const categories = ["All", "Calcium Removal", "Pool Prep", "Surface Restoration"];

const projects = [
  {
    id: 1,
    title: "Pool Tile Calcium Removal",
    category: "Calcium Removal",
    location: "Austin, TX",
    image: images.blueTileAfter,
    beforeImage: images.blueTileBefore,
    afterImage: images.blueTileAfter,
    description: "Complete calcium removal from residential pool tiles. Restored original tile color and finish without damage to grout or tile surface.",
    hasBeforeAfter: true,
  },
  {
    id: 2,
    title: "Stone Wall Restoration",
    category: "Surface Restoration",
    location: "Austin, TX",
    image: images.stoneAfter,
    beforeImage: images.stoneBefore,
    afterImage: images.stoneAfter,
    description: "Exterior stone and masonry wall restoration. Removed years of buildup to reveal clean, restored surface.",
    hasBeforeAfter: true,
  },
  {
    id: 3,
    title: "Waterline Tile Restoration",
    category: "Pool Prep",
    location: "Austin, TX",
    image: images.poolPrepAfter,
    beforeImage: images.poolPrepBefore,
    afterImage: images.poolPrepAfter,
    description: "Pool waterline tile cleaning and surface prep. Removed calcium deposits from decorative tile band for replastering preparation.",
    hasBeforeAfter: true,
  },
  {
    id: 4,
    title: "Pool Calcium Buildup",
    category: "Calcium Removal",
    location: "Austin, TX",
    image: images.blueTileBefore,
    description: "Example of heavy calcium buildup on pool tiles before our media blasting treatment. Austin's hard water makes this a common issue.",
    hasBeforeAfter: false,
  },
  {
    id: 5,
    title: "Pool Prep Project",
    category: "Pool Prep",
    location: "Austin, TX",
    image: images.poolPrepBefore,
    description: "Pool surface showing calcium deposits on waterline tile before restoration. This level of buildup requires precision blasting.",
    hasBeforeAfter: false,
  },
];

const Work = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects =
    activeCategory === "All"
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  return (
    <div data-testid="work-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-surface">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } },
            }}
            className="max-w-4xl"
          >
            <motion.span variants={fadeUp} transition={{ duration: 0.6 }} className="label-accent block mb-6">
              Portfolio
            </motion.span>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight mb-8"
            >
              Our
              <br />
              <span className="text-text-secondary">Work</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              Real projects from Austin and surrounding areas. Every image shows actual transformation results from our precision media blasting.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="section-spacing">
        <div className="container-custom">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap gap-4 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase().replace(/ /g, '-')}`}
                className={`px-6 py-3 font-mono uppercase tracking-widest text-xs transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-accent text-white"
                    : "bg-surface border border-white/10 text-text-secondary hover:text-text-primary hover:border-white/30"
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  data-testid={`project-${project.id}`}
                  className="group cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden mb-4">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-background/30 group-hover:bg-background/10 transition-colors duration-500" />
                    <div className="absolute bottom-4 left-4 flex gap-2">
                      <span className="label-accent bg-background/80 px-3 py-1">
                        {project.category}
                      </span>
                      {project.hasBeforeAfter && (
                        <span className="px-3 py-1 bg-accent text-white text-xs font-mono uppercase tracking-wider">
                          Before/After
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-1 group-hover:text-accent transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-text-secondary text-sm">{project.location}</p>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/95 overflow-y-auto"
            onClick={() => setSelectedProject(null)}
            data-testid="project-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full bg-surface border border-white/10 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedProject(null)}
                data-testid="modal-close"
                className="absolute top-4 right-4 z-10 p-2 text-text-secondary hover:text-text-primary transition-colors bg-background/50"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="p-6 md:p-10">
                <div className="mb-6">
                  <span className="label-accent block mb-2">{selectedProject.category}</span>
                  <h3 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-2">
                    {selectedProject.title}
                  </h3>
                  <p className="text-text-secondary text-sm">{selectedProject.location}</p>
                </div>

                {selectedProject.hasBeforeAfter ? (
                  <div className="mb-8">
                    <BeforeAfterSlider
                      beforeImage={selectedProject.beforeImage}
                      afterImage={selectedProject.afterImage}
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] overflow-hidden mb-8">
                    <img
                      src={selectedProject.image}
                      alt={selectedProject.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <p className="text-text-secondary leading-relaxed mb-8">
                  {selectedProject.description}
                </p>

                <Link
                  to="/quote"
                  data-testid="modal-quote-link"
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={() => setSelectedProject(null)}
                >
                  Start Similar Project
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="section-spacing bg-surface">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-accent block mb-6">Your Project</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-6">
              Ready to transform your surfaces?
            </h2>
            <p className="text-text-secondary text-lg mb-10">
              Get a free quote for your pool tile, stone, or specialty blasting project.
            </p>
            <Link
              to="/quote"
              data-testid="work-cta-quote"
              className="btn-primary inline-flex items-center gap-2"
            >
              Get Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Work;
